import React from "react";
import { DotLegalBreadCrumbModel, DotLegalCompletedPercentage, DotLegalEmptyState, DotLegalPaper } from "@dotlegal/dotlegal-ui-components";
import { Box } from "@mui/material";
import Grid from "@mui/material/Grid2";
import EntityDetailTopBar from "@common/components/entityDetailTopBar/EntityDetailTopBar";
import TopBarRow from "@common/components/entityDetailTopBar/TopBarRow";
import TopBarNumberTile from "@common/components/entityDetailTopBar/TopBarNumberTile";
import TopBarTile, { useTopBarTileSize } from "@common/components/entityDetailTopBar/TopBarTile";
import TopBarRiskLevelAndScoreTile from "@common/components/entityDetailTopBar/TopBarRiskLevelAndScoreTile";
import InternalAssessmentGroupTopBarRow from "../internalAssessmentGroupTopBar/InternalAssessmentGroupTopBarRow";
import PlanTabs, { PlanTabContent } from "../../plan/planTab/PlanTabs";
import { getPlanTab } from "../../plan/planTab/PlanTab";
import RelatedTasksTab from "../../taskManagement/relatedTasksTab/RelatedTasksTab";
import { useGetRelatedTaskData } from "../../taskManagement/relatedTasksTab/RelatedTasksTab.hooks";
import { usePlanContext } from "../../auth/planProvider/PlanProvider";
import { EntityType } from "@common/Common.types";
import { InternalAssessmentGroupTabs } from "../InternalAssessmentGroups.types";
import InternalAssessmentTable from "@common/components/questionnaireComponents/internalAssessmentTable/InternalAssessmentTable";
import EmptyState from "@common/svgs/emptyState.svg?react";
import { useTranslation } from "@localization/useTranslation";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import { useUrlProvider } from "../../useUrlProvider";
import InternalAssessmentRowActions from "../internalAssessmentRowActions/InternalAssessmentRowActions";
import InternalAssessmentGroupHeader from "../internalAssessmentGroupHeader/InternalAssessmentGroupHeader";
import ManagementReportButtons from "@common/components/managementReport/ManagementReportButtons";
import useInternalAssessmentGroupOverview from "./InternalAssessmentGroupOverview.hooks";

function InternalAssessmentGroupOverview() {
    const { permissions, customerName } = useUserContext();
    const { addonsFeatures, platformFeatures, vendorManagementFeatures } = usePlanContext();
    const { translateString } = useTranslation();
    const { getInternalAssessmentGroupsUrl, getInternalAssessmentGroupTaskApiUrl, getInternalAssessmentGroupManagementReportUrl } = useUrlProvider();
    const { id, data, isLoading, refetch, rowActions, selectedTab, setSelectedTab } = useInternalAssessmentGroupOverview();
    const { getTaskTabHeader } = useGetRelatedTaskData(id!, getInternalAssessmentGroupTaskApiUrl(id!));
    const tileSize = useTopBarTileSize();

    const getBreadCrumbs = () => {
        const breadCrumbs: Array<DotLegalBreadCrumbModel> = [];

        breadCrumbs.push({ name: translateString("internalAssessments"), link: getInternalAssessmentGroupsUrl() });
        breadCrumbs.push({ name: data?.name });

        return breadCrumbs;
    };

    const getTabContent = () => {
        const content: Array<PlanTabContent> = [];

        content.push({
            label: translateString("internalAssessments"),
            content: (
                <DotLegalPaper>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <InternalAssessmentTable
                            emptyState={<DotLegalEmptyState icon={<EmptyState />} text={translateString("noInternalAssessments")} />}
                            internalAssessmentModel={data?.assessments}
                            isLoading={isLoading}
                            refetch={() => refetch()}
                            getMenuItems={(row) => rowActions.getMenuItems(row)}
                            sessionStorageKey="assessment-group-assessments-filters"
                            withinAssessmentGroup
                        />

                        {/* A group that has not been sent has no answers to report on. */}
                        {data?.sent && (
                            <ManagementReportButtons
                                reportUrl={getInternalAssessmentGroupManagementReportUrl(data.id)}
                                groupName={data.name}
                                sender={data.sender ?? customerName}
                                enabled={vendorManagementFeatures.internalAssessmentGroups}
                                lockedText={translateString("upgradeVendorManagementPlan")}
                            />
                        )}
                    </Box>
                </DotLegalPaper>
            ),
            index: InternalAssessmentGroupTabs.Assessments,
        });

        if (permissions.taskPermissions.read && !addonsFeatures.research) {
            content.push(
                getPlanTab(
                    getTaskTabHeader(),
                    <RelatedTasksTab
                        forEntityId={id!}
                        forEntityType={EntityType.InternalAssessmentGroup}
                        forEntityName={data?.name ?? ""}
                        customApiUrl={getInternalAssessmentGroupTaskApiUrl(id!)}
                        additionalHeaderTypeTableHeader={translateString("createdFrom")}
                    />,
                    InternalAssessmentGroupTabs.Tasks,
                    platformFeatures.canAccessRelationsBetweenEntities,
                    translateString("upgradePrivacyPlanToday")
                )
            );
        }

        return content;
    };

    const getStatus = () => {
        if (!data) return "";
        if (!data.sent) return translateString("draft");

        return data.progress === 100 ? translateString("completed") : translateString("inProgress");
    };

    return (
        <React.Fragment>
            <InternalAssessmentGroupHeader
                breadCrumbs={getBreadCrumbs()}
                internalAssessmentGroup={data}
                showAddRecipientsMenuItem
                showDeleteMenuItem
                canBeDeleted={data?.canBeDeleted ?? false}
                onInternalAssessmentGroupUpdated={() => refetch()}
            />

            <Box sx={{ marginBottom: 2 }}>
                <EntityDetailTopBar>
                    <InternalAssessmentGroupTopBarRow data={data} isLoading={isLoading} status={getStatus()} />
                    <TopBarRow>
                        <TopBarNumberTile label={translateString("recipients")} isLoading={isLoading} value={data?.numberOfAssignees} />
                        <TopBarNumberTile label={translateString("opened")} isLoading={isLoading} value={data?.openedCount} />
                        <TopBarNumberTile label={translateString("submitted")} isLoading={isLoading} value={data?.submittedCount} />
                        <TopBarNumberTile label={translateString("excluded")} isLoading={isLoading} value={data?.excludedCount} />
                        {data && data.scoreEnabled && (
                            <TopBarRiskLevelAndScoreTile
                                isLoading={isLoading}
                                scoreEnabled={data.scoreEnabled}
                                riskLevel={data.overallRiskLevel}
                                riskLevelName={data.overallRiskLevelName}
                                score={data.overallRiskScore}
                            />
                        )}
                        <TopBarTile label={translateString("progress")} isLoading={isLoading}>
                            {data && (
                                <Box sx={{ display: "flex", justifyContent: "center" }}>
                                    <Box width={238}>
                                        <DotLegalCompletedPercentage completed={data.progress} />
                                    </Box>
                                </Box>
                            )}
                        </TopBarTile>
                        {data && !data.scoreEnabled && <Grid size={tileSize} />}
                    </TopBarRow>
                </EntityDetailTopBar>
            </Box>

            <PlanTabs
                fullWidth={false}
                tabStyle="secondary"
                selectedTab={selectedTab!}
                setSelectedTab={(value) => setSelectedTab(value)}
                content={getTabContent()}
                noPadding
                ignoreTextTransformation
            />

            <InternalAssessmentRowActions rowActions={rowActions} />
        </React.Fragment>
    );
}

export default InternalAssessmentGroupOverview;
